import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  automationActionByContext,
  AutomationContext,
  IAutomationAction,
} from '@kinet-open-network/shared/data-access/model';
import { FieldType, FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';
import { ButtonModule } from 'primeng/button';
import { startWith } from 'rxjs';
import { AutomationActionContainerComponent } from './automation-action-container.component';
import { AutomationActionService } from './automation-action.service';

interface AutomationActionBuilderProps extends FormlyFieldProps {
  context?: AutomationContext;
}

@Component({
  selector: 'kon-automation-action-builder-type',
  imports: [ButtonModule, AutomationActionContainerComponent],
  providers: [AutomationActionService],
  template: `
    <div class="flex flex-col gap-4">
      @for (action of actions(); track action.id) {
        <kon-automation-action-container
          [context]="props.context"
          [index]="$index"
          [action]="action"
          (actionChange)="handleActionChange($index, $event)"
          (actionRemove)="handleActionRemove($index)"
        />
      } @empty {
        <p class="text-gray-400">Žiadne akcie</p>
      }
    </div>

    <div class="flex items-center gap-2 mt-2">
      <button
        pButton
        type="button"
        label="Pridať akciu"
        icon="pi pi-plus"
        size="small"
        [outlined]="true"
        (click)="addAction()"
      ></button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutomationActionBuilderTypeComponent
  extends FieldType<FieldTypeConfig<AutomationActionBuilderProps>>
  implements OnInit
{
  readonly #actionService = inject(AutomationActionService);
  readonly #destroyRef = inject(DestroyRef);

  readonly actions = signal<IAutomationAction[]>([]);

  ngOnInit() {
    this.formControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        startWith(this.formControl.value),
      )
      .subscribe((value) => {
        this.actions.set(value ? (value as IAutomationAction[]) : []);
      });
  }

  addAction() {
    const enabledActions = this.props.context
      ? automationActionByContext[this.props.context]
      : null;

    this.formControl.setValue([
      ...this.actions(),
      this.#actionService.createAction(
        enabledActions ? enabledActions[0] : undefined,
      ),
    ]);
  }

  handleActionChange(index: number, action: Partial<IAutomationAction>) {
    const actions = this.actions();
    actions[index] = action as IAutomationAction;
    this.formControl.setValue([...actions]);
  }

  handleActionRemove(index: number) {
    const actions = this.actions();
    actions.splice(index, 1);
    this.formControl.setValue([...actions]);
  }
}
